import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { route } from '@services/symfony';
import { gsspGetLocale } from '@utils/gssp';

import { SanitizeHTML } from '@components/common/SanitizeHTML/SanitizeHTML';
import {
  ChatTypingDots, Button, Chip,
} from '@components/common';
import { useChatComposer } from '@hooks/useChatComposer/useChatComposer';
import React, {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';

import {
  PaperAirplaneIcon, MicrophoneIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon,
} from '@heroicons/react/24/solid';
import { ChatTextarea } from '@components/common/Chat/ChatTextarea';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { Message, useStreamState } from '@components/templates/bia/hooks/useStreamState';
import { v4 as uuidv4 } from 'uuid';
import { LayoutWithHeader } from '@components/layout/Layouts/LayoutWithHeader';
import type { JSX } from 'react';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import { ChatJobModal } from '@components/templates/homepage/chat/ChatJobModal';
import { Footer } from '@components/layout';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { useAppSelector } from '@store/index';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import NiceModal from '@ebay/nice-modal-react';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
// import { StartAiInterviewChatButton } from '@components/templates/ai-interview';
import ChatTalentModal from '@root/src/components/templates/homepage/chat/ChatTalentModal';
// import BestieAvatar from '@components/templates/bestie/BestieAvatar';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { cn } from '@utils/cn';
import { Transition } from '@headlessui/react';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { useSwrResumePersonalData } from '@hooks/useSwrApi/endpoints/resume/useSwrResumePersonalData';
import { StoriesSection } from '@components/templates/homepage/StoriesSection';
import { CompaniesSlider } from '@components/features/cards/employer/CompaniesSlider';
import { useSwrResumeLastWorkExperience } from '@hooks/useSwrApi/endpoints/resume';
import { useSwrJobSearchTooltip } from '@hooks/useSwrApi/endpoints/job';
import { showVoiceAgentModalWithAuth } from '@utils/voice-agent/showVoiceAgentModalWithAuth';

// Interface
// ************************************************************************************
interface HomepageProps {
  locale: Locale;
  clientCity: string;
}
/**
 * @description Homepage component
 * - **locale** - The locale of the app
 * - **eventsFeedServer** - The events list from GSSP
 * - **tagCloud** - The tag cloud list from GSSP
 * - **timestamp** - The timestamp for the events feed API call (in seconds)
 */
const Index = (props: HomepageProps) => {
  // Destructure props
  const { locale, clientCity } = props;

  // The following script tag contains JSON-LD (JavaScript Object Notation for Linked Data)
  // structured data with the application/ld+json MIME type. JSON-LD is used to provide
  // machine-readable information about the website, enhancing its presentation
  // in search engine results and enabling better indexing by search engines.
  const ORG_SCHEMA = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: 'https://www.bestjobs.eu/',
    name: 'BestJobs',
    // SiteLinks Search Box
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `https://www.bestjobs.eu${route('app_job_canonical')}/{search_term_string}?utm_source=google&utm_medium=organic%20search`,
      },
      'query-input': 'required name=search_term_string',
    },
  });

  const agent = {
    id: '', // leave blank in order to use unified chat agent (hr + search)
  };
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  // Fetch authentication status and personal data early to resolve city
  const { headerInfoData } = useHeaderInfoData(locale);
  const isAuthenticated = !!headerInfoData;

  // Fetch personal data only if authenticated
  const { data: personalData } = useSwrResumePersonalData({
    locale,
    shouldFetch: isAuthenticated,
  });

  // Fetch last search keyword and last experience for personalization
  const { data: jobSearchTooltip } = useSwrJobSearchTooltip({
    locale,
    shouldFetch: isAuthenticated,
  });
  const { data: lastWorkExperience } = useSwrResumeLastWorkExperience({
    locale,
    shouldFetch: isAuthenticated,
  });

  // Resolve city value based on authentication and personal data
  const resolvedCity = useMemo(() => {
    if (!isAuthenticated) {
      // Not authenticated: use x-client-city header
      return clientCity;
    }

    // Authenticated: check resumeLocation from personal data
    const resumeLocation = personalData?.resumeLocation;

    if (!resumeLocation || resumeLocation.trim() === '') {
      // No location in personal data: use x-client-city header
      return clientCity;
    }

    // Use resumeLocation from personal data
    return resumeLocation;
  }, [isAuthenticated, personalData, clientCity]);

  const mainActionButtons = [
    { label: t('homepage.chip.find_job'), key: 'homepage.chip.find_job' },
    { label: t('homepage.chip.update_profile'), key: 'homepage.chip.update_profile' },
    { label: t('homepage.chip.career_advice'), key: 'homepage.chip.career_advice' },
    { label: t('homepage.chip.hire_with_bestie'), key: 'homepage.chip.hire_with_bestie' },
  ];

  const getSubOptions = (categoryKey: string, cityValue: string) => {
    const maxOptions = 4;
    const options = [];

    // Special handling for "Find jobs" category - personalize first sub-suggestion
    if (categoryKey === 'homepage.chip.find_job') {
      let keyword = '';
      const recentKeyword = jobSearchTooltip?.recentSearches?.[0]?.keyword
        || jobSearchTooltip?.recentSearches?.[0]?.jobSearch?.keyword;
      const savedKeyword = jobSearchTooltip?.savedSearches?.[0]?.keyword
        || jobSearchTooltip?.savedSearches?.[0]?.jobSearch?.keyword;

      // Priority 1: Last search keyword
      if (recentKeyword || savedKeyword) {
        keyword = String(recentKeyword || savedKeyword);
      } else if (lastWorkExperience?.title) {
        // Priority 2: Last experience
        keyword = String(lastWorkExperience.title);
      }

      // If we have a keyword, use the custom translation
      if (keyword) {
        const customLabel = t('homepage.chip.find_job_sub1_custom');
        const label = customLabel.replace('{{keyword}}', keyword);
        options.push({
          label,
          key: 'personalized_search',
        });
      }
      // Priority 3: Default - add sub1 normally below
    }

    for (let i = 1; i <= maxOptions; i++) {
      const key = `${categoryKey}_sub${i}`;
      let label = t(key);

      // i18next returns the key itself if translation is missing
      if (label !== key) {
        // For "Find jobs" category, skip sub1 if we already added a personalized option
        const skipPersonalizedSub1 = categoryKey === 'homepage.chip.find_job' && i === 1 && options.length > 0;

        if (!skipPersonalizedSub1) {
          // Replace {{city}} placeholder if present
          if (label.includes('{{city}}')) {
            label = label.replace('{{city}}', cityValue);
          }

          options.push({ label, key });
        }
      }
    }

    return options;
  };

  const {
    stream, startStream,
  } = useStreamState(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const initialMessageFromUrlSent = useRef(false);
  const anonSessionId = useAnonSessionId();
  const [talentsNavigationList, setTalentsNavigationList] = useState<ApiSchema<'ResumeCardData'>[]>([]);
  const [hasSubmittedFirstMessage, setHasSubmittedFirstMessage] = useState(false);
  const [firstMessageContent, setFirstMessageContent] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const headers = useMemo(() => ({
    'x-anon-session-id': anonSessionId,
  }), [anonSessionId]);

  const composer = useChatComposer({
    history: stream?.messages,
    onSubmit: async (text) => {
      if (!hasSubmittedFirstMessage) {
        setFirstMessageContent(text);
      }
      setHasSubmittedFirstMessage(true);
      const message = { id: uuidv4(), content: text, type: 'human' } as const;
      await startStream(message, agent.id || '', {
        headers,
        configurations: { user_locale: locale || 'ro' },
      });
    },
  });

  useEffect(() => {
    if (initialMessageFromUrlSent.current || typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    const initialQuery = searchParams.get('q');
    const isFromInterview = searchParams.get('iw');

    // Determine initial message content
    let messageContent: string | null = null;
    if (isFromInterview === '1') {
      messageContent = t('bestie.start.text.interview.prompt');
    } else if (initialQuery) {
      messageContent = initialQuery;
    }

    if (!messageContent) return;

    const message = {
      id: uuidv4(),
      content: messageContent,
      type: 'human',
    };

    setHasSubmittedFirstMessage(true);
    void startStream(message, agent.id || '', {
      headers,
      configurations: { user_locale: locale || 'ro' },
    });

    router.replace('/bestie', { scroll: false });
    initialMessageFromUrlSent.current = true;
  }, [router, locale, startStream, agent.id, headers, anonSessionId, t]);

  // Click outside to reset to main options
  useEffect(() => {
    if (!selectedCategory) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is outside the chips and form area
      const isChipClick = target.closest('[data-chip-area]');
      const isFormClick = target.closest('form');

      if (!isChipClick && !isFormClick) {
        setSelectedCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedCategory]);

  // Combined effect: scroll to bottom and clear firstMessageContent when messages load
  useEffect(() => {
    if (stream?.messages && stream.messages.length > 0) {
      // Scroll to bottom when messages update
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
      // Clear firstMessageContent to prevent duplication
      setFirstMessageContent(null);
    }
  }, [stream?.messages]);

  const jobsArray = useMemo(() => stream?.messages
    ?.filter((item) => (item?.response_metadata?.customData?.jobSearchResults?.items
      && item?.response_metadata?.customData?.jobSearchResults?.items?.length > 0))
    .reduce<{ items: ApiSchema<'JobItem'>[] | undefined }[]>((acc, item) => {
    acc.push({ items: item?.response_metadata?.customData?.jobSearchResults?.items?.map((i) => i.data) });
    return acc;
  }, []), [stream?.messages]);

  const [expandState, setExpandState] = useState<{ showToggle: boolean; isExpanded: boolean; toggle:() => void } | null>(null);

  const formElements = (
    <div className="w-full max-w-5xl">
      <div className={cn(
        'group relative flex items-end rounded-2xl border-2 bg-surface p-0.5 transition-all duration-700',
        hasSubmittedFirstMessage ? 'shadow-sm' : 'border-secondary shadow-lg shadow-secondary/30',
      )}
      >
        <ChatTextarea
          textareaRef={composer.textareaRef}
          input={composer.input}
          handleInputChange={composer.onChange}
          handleKeyDown={composer.onKeyDown}
          placeholder={t('homepage.chat.input.placeholder')}
          customWrapperStyle="flex-1 bg-white rounded-2xl"
          customTextareaStyle="transition-none bg-white text-gray-900 placeholder:text-gray-400 placeholder:text-base px-2 text-lg focus:ring-0 focus:ring-offset-0 outline-none border-none shadow-none whitespace-normal resize-none cursor-text pointer-events-auto placeholder:truncate placeholder:overflow-ellipsis leading-normal py-1.5 break-words"
          textareaSizeStyle="w-full min-h-10"
          textareaWrapperSizeStyle="w-full min-h-10"
          textareaAlignmentStyle="px-2 py-1.5"
          collapsedLineLimit={6}
          expandedLineLimit={15}
          onExpandStateChange={setExpandState}
        />
        {/* Right column: expand button above airplane button */}
        <div className="mr-1 flex shrink-0 flex-col items-center self-stretch">
          {expandState?.showToggle && (
            <button
              type="button"
              onClick={expandState.toggle}
              className="mt-1 shrink-0 rounded p-1 text-ink transition-colors hover:text-ink"
              aria-label={expandState.isExpanded ? 'Collapse' : 'Expand'}
            >
              {expandState.isExpanded
                ? <ArrowsPointingInIcon className="size-5" />
                : <ArrowsPointingOutIcon className="size-5" />}
            </button>
          )}
          <Button
            type="button"
            color="secondary"
            rounding="none"
            className="my-1 mt-auto size-12 shrink-0 rounded-2xl !p-3"
            onClick={() => {
              // Show microphone modal if no text and no first message sent yet
              if (!composer.input.trim()) {
                sendGoogleTagEvent('bestie_conversations_banner_call', 'Custom');
                showVoiceAgentModalWithAuth(agent.id, locale);
              } else {
                // Otherwise submit the text message
                void composer.submit();
              }
            }}
          >
            {!composer.input.trim() ? (
              <MicrophoneIcon className="size-5 text-white" />
            ) : (
              <PaperAirplaneIcon className="size-5 text-white" />
            )}
          </Button>
        </div>
      </div>
      {!hasSubmittedFirstMessage && (
        <div className="mb-2 mt-4 flex w-full flex-col gap-3 px-2 md:max-w-5xl md:px-0" data-chip-area>
          <div className={cn(
            'flex w-full gap-2',
            selectedCategory ? 'flex-col items-start' : 'flex-wrap justify-center',
          )}
          >
            {selectedCategory ? (
              // Show sub-options when a category is selected (vertical)
              getSubOptions(selectedCategory, resolvedCity).map((btn) => (
                <Chip
                  key={btn.key}
                  onClick={() => {
                    if (!hasSubmittedFirstMessage) {
                      setFirstMessageContent(btn.label);
                    }
                    setHasSubmittedFirstMessage(true);
                    const message = { id: uuidv4(), content: btn.label, type: 'human' } as const;
                    void startStream(message, agent.id || '', {
                      headers,
                      configurations: { user_locale: locale || 'ro' },
                    });
                  }}
                  className="whitespace-normal rounded-2xl bg-surface px-3 py-2 text-left text-sm leading-relaxed text-ink shadow-sm hover:border-0 hover:!border-transparent hover:bg-secondary/75 hover:!text-surface md:text-base"
                >
                  {btn.label}
                </Chip>
              ))
            ) : (
              // Show main category chips when nothing is selected
              <>
                {mainActionButtons.map((btn) => (
                  <Chip
                    key={btn.key}
                    onClick={() => {
                      setSelectedCategory(btn.key);
                    }}
                    className="rounded-2xl bg-surface px-3 py-1.5 text-ink shadow-sm hover:border-0 hover:!border-transparent hover:bg-secondary/75 hover:!text-surface md:px-4 md:py-2"
                  >
                    {btn.label}
                  </Chip>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale });
    }
  }, [bulkApplyFeed, locale]);

  const onCvDetailOpen = useCallback((message: Message) => {
    setTalentsNavigationList(message.response_metadata?.customData?.resumeSearchResults?.items || []);
  }, []);

  const messagesList = useMemo(() => {
    if (!stream?.messages) return [];

    // Use a Map to remove duplicates based on message id
    return [...new Map(stream.messages.map((m) => [m.id, m])).values()]
      .reverse()
      .map((m, index) => (
        <div key={`message-container-${m.id}`} className="flex flex-col gap-4">
          <ChatMessageComponent
            key={`message-${m.id}`}
            message={{ ...m }}
            ref={index === 0 ? lastMessageRef : null}
            locale={locale}
            onInteraction={onCvDetailOpen}
          />
        </div>
      ));
  }, [stream?.messages, locale, onCvDetailOpen]);

  // Render page
  // ******************************
  return (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>
          {t('homepage.react.meta.title')}
        </title>
        <meta name="description" content={t('meta.homepage.description_react')} />
        <SanitizeHTML as="script" type="application/ld+json" htmlContent={ORG_SCHEMA} />
      </Head>


      {/* PAGE CONTENT */}
      <div className="flex grow flex-col bg-surface-100">
        {stream?.messages && stream.messages?.length > 0 ? (
          <div className="flex h-full grow rounded">
            <div
              className="container mx-auto flex h-full flex-col items-center overflow-y-auto rounded p-4"
            >
              <div
                className="flex size-full grow flex-col-reverse gap-5 pb-48 md:pb-40"
                ref={messageContainerRef}
              >
                {stream?.status === 'inflight' && <ChatTypingDots />}
                { messagesList }
              </div>

              <ChatJobModal locale={locale} jobsArray={jobsArray} />
              <ChatTalentModal talentsArray={[{ items: talentsNavigationList }]} />
              <form
                onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
                className="fixed bottom-16 left-1/2 z-[5] w-11/12 max-w-3xl -translate-x-1/2 md:bottom-5"
              >
                {formElements}
              </form>
            </div>
          </div>
        ) : (
          <>
            <div className="container mx-auto flex min-h-[calc(100vh-180px)] w-full flex-col">
              {/* Hero Section - centered when no messages */}
              <div className={cn(
                'flex w-full flex-col items-center justify-start pt-0 transition-all duration-[400ms] md:pt-12',
                hasSubmittedFirstMessage ? 'grow-0' : 'grow',
              )}
              >
                {/* Animated first message bubble */}
                <Transition
                  as="div"
                  show={hasSubmittedFirstMessage && !!firstMessageContent}
                  enter="transition-all duration-700 ease-out"
                  enterFrom="opacity-0 translate-y-4 scale-95"
                  enterTo="opacity-100 translate-y-0 scale-100"
                  className="mb-4 w-full max-w-3xl"
                >
                  <div className="ml-auto w-fit max-w-[80%] rounded-2xl bg-secondary px-4 py-2 text-white">
                    {firstMessageContent}
                  </div>
                </Transition>

                {/* Desktop Layout - Title only */}
                <div className="relative hidden w-full md:flex md:justify-center">
                  <div className="flex items-start gap-0">
                    <div className="flex w-[64rem] flex-col items-center gap-2">
                      <span className="text-3xl font-bold">{t('homepage.hero.title')}</span>
                      <span className="text-lg">{t('homepage.hero.subtitle')}</span>
                    </div>
                  </div>
                </div>

                {/* Mobile Layout - Title only */}
                <div className="flex w-full flex-col items-center text-center md:hidden">
                  <div className="mb-2 flex items-center">
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="mt-4 text-2xl font-bold">{t('homepage.hero.title')}</span>
                      <span className="text-base">{t('homepage.hero.subtitle')}</span>
                    </div>
                  </div>
                </div>

                {/* Single form instance - shared between desktop and mobile */}
                <form
                  onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
                  className={cn(
                    'mt-4 w-full max-w-5xl mx-auto transition-all duration-[400ms] ease-in-out',
                    hasSubmittedFirstMessage && 'fixed bottom-16 left-1/2 z-[5] w-11/12 max-w-5xl -translate-x-1/2 md:bottom-5',
                  )}
                >
                  {formElements}
                </form>
              </div>
              <div className="mt-8">
                <StoriesSection />
              </div>

              <div className="my-3">
                <CompaniesSlider />
              </div>
            </div>
            <Footer className="pb-28 md:pb-35" />
          </>
        )}
      </div>
    </>
  );
};

/**
 * @description Get the layout for the page
 * @param page
 */
Index.getLayout = function getLayout(page: JSX.Element) {
  return (<LayoutWithHeader>{page}</LayoutWithHeader>);
};


/**
 * @description Get the server-side props
 * @param context - The context object
 */
export const getServerSideProps: GetServerSideProps = async (context) => {
  // Props destructuring
  const { params, req } = context;

  const locale = gsspGetLocale(params);

  // Extract x-client-city header
  const clientCity = (req.headers['x-client-city'] as string) || '';

  // Return the props
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      clientCity,
    },
  };
};


/**
 * @description Export the page component.
 */
export default Index;
